import { DEFAULT_LANGUAGE } from './constants';

/**
 * A locale map as stored in the database: BCP-47 tag -> translated value.
 *
 * ```json
 * { "pt-BR": "Sala de espera", "en-US": "Waiting room", "es-419": "" }
 * ```
 */
export type I18nMap<T = string> = Record<string, T>;

const isNonEmptyString = (entry: unknown): entry is string =>
  typeof entry === 'string' && entry !== '';

const isNonEmptyStringArray = (entry: unknown): entry is Array<string> =>
  Array.isArray(entry) && entry.length > 0;

/**
 * Walks `languages` in order and returns the first entry accepted by
 * `isUsable`, falling back to the default language before giving up.
 */
const pickEntry = <T>(
  map: Record<string, unknown>,
  languages: ReadonlyArray<string>,
  isUsable: (entry: unknown) => entry is T,
): T | undefined => {
  const preferred = languages.map((language) => map[language]).find(isUsable);
  if (preferred !== undefined) return preferred;

  const fallback = map[DEFAULT_LANGUAGE];
  return isUsable(fallback) ? fallback : undefined;
};

/**
 * Picks the best available translation from a locale map of strings, given an
 * ordered list of preferred languages.
 *
 * Top-level `*I18N` columns (`buttonLabelI18N`, `titleI18N`, …) are already
 * resolved server-side from the `Accept-Language` header, so they arrive as
 * plain strings and must NOT go through this helper. Locale maps nested inside
 * a JSONB column (e.g. `data.descriptionI18N`) are opaque to the API's `@i18n`
 * plugin and reach the client raw — those are what this helper is for.
 *
 * Mirrors `resolveI18n` in `apps/inpatient-api/src/utils/i18n.ts` so both sides
 * pick the same value, with one deliberate addition: a plain string is returned
 * as-is, so rows not yet migrated to a locale map still render.
 *
 * ### Resolution order
 * 1. First language in `languages` with a non-empty entry in the map.
 * 2. `pt-BR` as a hard fallback, even when absent from `languages`.
 * 3. `''` when the map exists but holds no usable entry.
 * 4. `null` when `value` is `null`/`undefined` (preserves column nullability).
 *
 * Prefer `useI18nResolvers().resolveI18n` inside components — it feeds the current
 * language in and re-renders on language change.
 *
 * @example
 * resolveI18n({ 'pt-BR': 'Sala', 'en-US': 'Room' }, ['en-US']); // 'Room'
 * resolveI18n({ 'pt-BR': 'Sala' }, ['es-419']);                  // 'Sala'
 * resolveI18n({ 'pt-BR': '' }, ['en-US']);                      // ''
 * resolveI18n(undefined, ['en-US']);                            // null
 */
export const resolveI18n = (
  value: unknown,
  languages: ReadonlyArray<string>,
): string | null => {
  // Absent value — propagate null so callers can tell "no data" from "no
  // translation".
  if (value === null || value === undefined) return null;

  // Not yet migrated to a locale map: the raw string is the translation.
  if (typeof value === 'string') return value;

  // Anything else (number, array, …) is not a valid locale map.
  if (typeof value !== 'object' || Array.isArray(value)) return '';

  return (
    pickEntry(value as Record<string, unknown>, languages, isNonEmptyString) ??
    ''
  );
};

/**
 * Same as {@link resolveI18n}, but for locale maps whose entries are lists
 * (e.g. `data.improvementsI18N` -> `{ 'pt-BR': ['Item 1', 'Item 2'] }`).
 *
 * Returns `null` — rather than `[]` — whenever no usable list is found, so
 * callers can fall back to a legacy untranslated field with a plain `??`
 * instead of having to length-check a truthy empty array.
 *
 * Prefer `useI18nResolvers().resolveI18nList` inside components.
 *
 * @example
 * resolveI18nList({ 'pt-BR': ['a'], 'en-US': ['b'] }, ['en-US']); // ['b']
 * resolveI18nList({ 'pt-BR': ['a'] }, ['es-419']);                 // ['a']
 * resolveI18nList({ 'pt-BR': [] }, ['en-US']);                    // null
 * resolveI18nList(undefined, ['en-US']);                          // null
 */
export const resolveI18nList = (
  value: unknown,
  languages: ReadonlyArray<string>,
): Array<string> | null => {
  if (value === null || value === undefined) return null;

  // Not yet migrated to a locale map: the raw list is the translation.
  if (Array.isArray(value)) {
    return value.length > 0 ? (value as Array<string>) : null;
  }

  if (typeof value !== 'object') return null;

  return (
    pickEntry(
      value as Record<string, unknown>,
      languages,
      isNonEmptyStringArray,
    ) ?? null
  );
};

export default resolveI18n;
